import QuickLRU from 'quick-lru';

import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import {
  type GqlSelectionListKey,
  gqlSelectionListKeys,
  type RoleSelectionListKey,
  type RoleSelectionLists,
  roleSelectionListKeys,
  type SelectionList,
  type SelectionListState,
} from '../../../../shared/selection-lists/types';
import { nestSelectionLists } from '../selectionList/nestSelectionLists';
import { sortCitySelectionList } from '../selectionList/sortCitySelectionList';
import { FilterId } from '../types/filters';

type NestedTreeResult = ReturnType<typeof nestSelectionLists>;

interface NestedTreeInput {
  filterId: FilterId;
  requiredLists: (GqlSelectionListKey | RoleSelectionListKey)[];
  selectionLists: SelectionListState<GqlSelectionListKey> | null;
  roleSelectionLists: RoleSelectionLists | null;
  roleTaxonomyValue?: CustomRoleTaxonomySelection; // Add this field
}

class NestedTreeBuilder {
  build(input: NestedTreeInput): NestedTreeResult | null {
    const { filterId, requiredLists, selectionLists, roleSelectionLists } =
      input;

    if (filterId === FilterId.CUSTOM_SKILL) return null;

    if (filterId === FilterId.ROLE && roleSelectionLists) {
      return this.buildRoleLists(requiredLists, roleSelectionLists);
    }

    if (selectionLists) {
      return this.buildGqlLists(requiredLists, selectionLists);
    }

    return null;
  }

  getCacheKey(input: NestedTreeInput): string {
    const lists = [...input.requiredLists].join(',');
    let key = `${input.filterId}:${lists}`;

    if (input.filterId === FilterId.ROLE && input.roleTaxonomyValue) {
      const taxonomyKey = JSON.stringify(input.roleTaxonomyValue);
      key += `:${taxonomyKey}`;
    }

    // Include selection list sizes so the cache invalidates when data arrives.
    // Without this, a cache entry built from partial data (e.g. only rics_k400)
    // blocks updates when full selection lists load.
    if (input.selectionLists) {
      const sizes = input.requiredLists
        .map((id) => {
          const list = (input.selectionLists as Record<string, unknown[]>)?.[
            id
          ];
          return Array.isArray(list) ? list.length : 0;
        })
        .join(',');
      key += `:${sizes}`;
    }

    return key;
  }

  private buildRoleLists(
    requiredLists: (GqlSelectionListKey | RoleSelectionListKey)[],
    roleSelectionLists: RoleSelectionLists
  ): NestedTreeResult | null {
    const roleKeys = requiredLists.filter((k): k is RoleSelectionListKey =>
      roleSelectionListKeys.includes(k as RoleSelectionListKey)
    );

    const relevantLists = roleKeys
      .map((listId) => ({
        selectionListId: listId,
        selectionList: (Array.isArray(roleSelectionLists[listId])
          ? roleSelectionLists[listId].filter((x) => x != null)
          : []) as SelectionList<RoleSelectionListKey>,
      }))
      .filter((list) => list.selectionList.length > 0);

    return relevantLists.length > 0 ? nestSelectionLists(relevantLists) : null;
  }

  private buildGqlLists(
    requiredLists: (GqlSelectionListKey | RoleSelectionListKey)[],
    selectionLists: SelectionListState<GqlSelectionListKey>
  ): NestedTreeResult | null {
    const gqlKeys = requiredLists.filter((k): k is GqlSelectionListKey =>
      gqlSelectionListKeys.includes(k as GqlSelectionListKey)
    );

    const relevantLists = gqlKeys.map((listId) => ({
      selectionListId: listId,
      selectionList:
        listId === 'city'
          ? sortCitySelectionList(selectionLists[listId] ?? [])
          : (selectionLists[listId] ?? []),
    }));

    return relevantLists.length > 1 ? nestSelectionLists(relevantLists) : null;
  }
}

class SelectionListCacheManager {
  private cache = new QuickLRU<string, NestedTreeResult>({ maxSize: 10 });
  private builder = new NestedTreeBuilder();

  getNestedTree(
    filterId: FilterId,
    requiredLists: (GqlSelectionListKey | RoleSelectionListKey)[],
    selectionLists: SelectionListState<GqlSelectionListKey> | null,
    roleSelectionLists: RoleSelectionLists | null,
    roleTaxonomyValue?: CustomRoleTaxonomySelection,
    isFetching?: boolean
  ): NestedTreeResult | null {
    const input: NestedTreeInput = {
      filterId,
      requiredLists,
      selectionLists,
      roleSelectionLists,
      roleTaxonomyValue,
    };

    const cacheKey = this.builder.getCacheKey(input);
    const cached = this.cache.get(cacheKey);
    if (cached !== undefined) {
      return cached;
    }

    const result = this.builder.build(input);
    if (result && !isFetching) {
      this.cache.set(cacheKey, result);
    }

    return result;
  }

  clear(): void {
    this.cache.clear();
  }
}

export const selectionListCache = new SelectionListCacheManager();
